/**
 * HARDER cart drawer — fetch-based cart operations, no page reloads.
 * Listens for 'cart:open' custom event to slide open.
 * Dispatches 'cart:updated' after every change so other components can react.
 */
(function() {
  'use strict';

  const SELECTORS = {
    drawer:    '.lp-cart-drawer',
    backdrop:  '.lp-cart-drawer__backdrop',
    close:     '[data-cart-close]',
    body:      '.lp-cart-drawer__body',
    itemsWrap: '.lp-cart-drawer__items',
    emptyState:'.lp-cart-drawer__empty',
    filledState:'.lp-cart-drawer__filled',
    footer:    '.lp-cart-drawer__footer',
    subtotal:  '[data-cart-subtotal]',
    itemCount: '[data-cart-count]',
    installments: '[data-cart-installments]',
    headerCount: '.lp-header__cart-count',
    progressBar: '[data-progress-bar]',
    progressFill:'[data-progress-fill]',
    progressMsg: '[data-progress-msg]',
    status:     '[data-cart-status]',
  };

  let isOpen = false;
  let cartData = null;
  let lastFocusedElement = null;
  let cartMutationQueue = Promise.resolve();
  let pendingItemKeys = new Set();
  let cartStateRevision = 0;
  let cartReadSequence = 0;
  let latestAppliedReadSequence = 0;

  /* ── Helpers ── */
  function formatMoney(cents) {
    var amount = (cents / 100);
    var locale = document.documentElement.lang || navigator.language || 'en';
    var currency = cartData && cartData.currency
      ? cartData.currency
      : window.Shopify && window.Shopify.currency && window.Shopify.currency.active
        ? window.Shopify.currency.active
        : 'USD';
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency
      }).format(amount);
    } catch (err) {
      return amount.toFixed(2) + ' ' + currency;
    }
  }

  function displayProductTitle(handle, fallbackTitle) {
    return fallbackTitle;
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function(character) {
      return {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
      }[character];
    });
  }

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return (ctx || document).querySelectorAll(sel); }

  function markCartStateChanged() {
    cartStateRevision += 1;
  }

  function updateHeaderCartState(count) {
    var headerBubble = $(SELECTORS.headerCount);
    if (headerBubble) {
      headerBubble.textContent = count;
      headerBubble.style.display = count > 0 ? '' : 'none';
    }

    var headerButton = $('#cart-icon-bubble');
    if (headerButton) {
      var cartLabel = headerButton.dataset.cartLabel || 'Open cart';
      var itemLabel = count === 1
        ? headerButton.dataset.cartItemLabel || 'item'
        : headerButton.dataset.cartItemsLabel || 'items';
      var accessibleLabel = cartLabel + ', ' + count + ' ' + itemLabel;
      headerButton.setAttribute('aria-label', accessibleLabel);
      var srText = headerButton.querySelector('.visually-hidden');
      if (srText) srText.textContent = accessibleLabel;
    }
  }

  function announceCartStatus(message) {
    var drawer = $(SELECTORS.drawer);
    var status = drawer ? $(SELECTORS.status, drawer) : null;
    if (!status || !message) return;
    status.textContent = '';
    window.requestAnimationFrame(function() {
      status.textContent = message;
    });
  }

  function refreshDrawerFocusTrap(drawer, preferredElement) {
    if (!drawer || !isOpen) return;
    var panel = $('.lp-cart-drawer__panel', drawer);
    var closeButton = $('.lp-cart-drawer__close-btn', drawer);
    if (!panel || !closeButton) return;
    var currentFocus = document.activeElement;
    var focusTarget = preferredElement ||
      (currentFocus instanceof HTMLElement && panel.contains(currentFocus) ? currentFocus : closeButton);
    if (typeof trapFocus === 'function') {
      trapFocus(panel, focusTarget);
    } else {
      focusTarget.focus();
    }
  }

  function findCartItemByKey(drawer, key) {
    return Array.from($$('.lp-cart-item[data-key]', drawer)).find(function(candidate) {
      return candidate.dataset.key === key;
    });
  }

  /* ── Open / Close ── */
  function open() {
    const drawer = $(SELECTORS.drawer);
    if (!drawer || isOpen) return;
    isOpen = true;
    lastFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    drawer.removeAttribute('inert');
    document.body.style.overflow = 'hidden';
    refreshDrawerFocusTrap(drawer, $('.lp-cart-drawer__close-btn', drawer));
    fetchAndRender();
  }

  function close() {
    const drawer = $(SELECTORS.drawer);
    if (!drawer || !isOpen) return;
    isOpen = false;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    drawer.setAttribute('inert', '');
    document.body.style.overflow = '';
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(lastFocusedElement);
    } else if (lastFocusedElement) {
      lastFocusedElement.focus();
    }
    lastFocusedElement = null;
  }

  /* ── Fetch cart state ── */
  async function fetchCart() {
    const res = await fetch('/cart.json', { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('Cart fetch failed');
    return res.json();
  }

  /* ── Cart API operations ── */
  async function changeItem(key, quantity, focusSelector) {
    markCartStateChanged();
    const res = await fetch('/cart/change.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity }),
    });
    if (!res.ok) throw new Error('Change failed');
    let nextCart = null;
    try {
      nextCart = await res.json();
    } catch (parseError) {
      console.warn('Cart change succeeded, but its response could not be parsed:', parseError);
      nextCart = await fetchAndRender();
    }
    if (!nextCart) return null;
    cartData = nextCart;
    dispatchUpdated(cartData);

    var drawer = $(SELECTORS.drawer);
    if (drawer) {
      announceCartStatus(
        quantity === 0
          ? drawer.dataset.itemRemovedText || 'Item removed'
          : drawer.dataset.cartUpdatedText || 'Cart updated'
      );
      window.requestAnimationFrame(function() {
        if (!isOpen) return;
        var matchingItem = findCartItemByKey(drawer, key);
        var fallbackItem = $('.lp-cart-item[data-key]', drawer);
        var focusTarget = matchingItem && focusSelector
          ? $(focusSelector, matchingItem)
          : fallbackItem && focusSelector
            ? $(focusSelector, fallbackItem)
            : null;
        refreshDrawerFocusTrap(drawer, focusTarget || $('.lp-cart-drawer__close-btn', drawer));
      });
    }
    return cartData;
  }

  async function addItem(variantId, quantity) {
    quantity = quantity || 1;
    markCartStateChanged();
    try {
      const res = await fetch('/cart/add.json', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: quantity }] }),
      });
      if (!res.ok) throw new Error('Add failed');
      let addedItem = null;
      try {
        addedItem = await res.json();
      } catch (parseError) {
        console.warn('Cart add succeeded, but its response could not be parsed:', parseError);
      }
      const latestCart = await fetchAndRender();
      if (latestCart) dispatchUpdated(latestCart);
      return addedItem;
    } catch (err) {
      console.error('Cart add error:', err);
      throw err;
    }
  }

  /* ── Dispatch event ── */
  function dispatchUpdated(cart) {
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart, bubbles: true }));
  }

  /* ── Fetch + render ── */
  async function fetchAndRender() {
    const requestRevision = cartStateRevision;
    const requestSequence = ++cartReadSequence;
    try {
      const cart = await fetchCart();
      if (
        requestRevision !== cartStateRevision ||
        requestSequence < latestAppliedReadSequence
      ) {
        return null;
      }
      latestAppliedReadSequence = requestSequence;
      cartData = cart;
      render(cart);
      return cart;
    } catch (err) {
      console.error('Cart fetch error:', err);
      if (isOpen) {
        var drawer = $(SELECTORS.drawer);
        announceCartStatus(drawer ? drawer.dataset.cartRefreshFailedText || 'Cart could not refresh' : 'Cart could not refresh');
      }
      return null;
    }
  }

  /* ── Render cart ── */
  function render(cart) {
    const drawer = $(SELECTORS.drawer);
    if (!drawer) return;

    const isEmpty = cart.item_count === 0;
    const emptyEl = $(SELECTORS.emptyState, drawer);
    const filledEl = $(SELECTORS.filledState, drawer);
    const footerEl = $(SELECTORS.footer, drawer);

    if (emptyEl) emptyEl.style.display = isEmpty ? '' : 'none';
    if (filledEl) filledEl.style.display = isEmpty ? 'none' : '';
    if (footerEl) footerEl.style.display = isEmpty ? 'none' : '';

    /* Update item count in drawer header */
    $$(SELECTORS.itemCount).forEach(function(el) {
      el.textContent = cart.item_count;
    });

    /* Update header cart bubble + screen-reader label */
    updateHeaderCartState(cart.item_count);

    /* Update subtotal */
    var subtotalEl = $(SELECTORS.subtotal, drawer);
    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);

    /* Update installments */
    var instEl = $(SELECTORS.installments, drawer);
    if (instEl) {
      instEl.textContent = formatMoney(Math.round(cart.total_price / 4));
      instEl.parentElement.style.display = isEmpty ? 'none' : '';
    }

    /* Render line items — then enrich with compare_at_price from product API */
    var itemsWrap = $(SELECTORS.itemsWrap, drawer);
    if (itemsWrap && !isEmpty) {
      var lockedGiftText = escapeHtml(drawer.dataset.lockedGiftText || 'Included with your product');
      var freeLabelText = escapeHtml(drawer.dataset.freeLabelText || 'Free');
      var decreaseLabel = escapeHtml(drawer.dataset.decreaseLabel || 'Decrease quantity');
      var increaseLabel = escapeHtml(drawer.dataset.increaseLabel || 'Increase quantity');
      var removeLabel = escapeHtml(drawer.dataset.removeLabel || 'Remove item');
      itemsWrap.innerHTML = cart.items.map(function(item) {
        var imgSrc = item.featured_image ? escapeHtml(item.featured_image.url.replace(/\.([^.]+)$/, '_200x200_crop_center.$1')) : '';
        var variantTitle = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
        var sellingPlanName = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan
          ? item.selling_plan_allocation.selling_plan.name
          : '';
        var isFree = item.final_line_price === 0;
        var giftAssist = $('[data-gift-assist]', drawer);
        var legacyGiftHandle = giftAssist ? (giftAssist.dataset.giftHandle || '') : '';
        var isLegacyGift = legacyGiftHandle && item.handle === legacyGiftHandle && item.original_price === 0;
        /* Initial render: use original_line_price fallback (compare_at comes later via enrichment) */
        var compareLine = null;
        if (item.original_line_price > item.final_line_price) {
          compareLine = item.original_line_price;
        }

        return '<div class="lp-cart-item" data-key="' + escapeHtml(item.key) + '" data-variant-id="' + escapeHtml(item.variant_id) + '" data-handle="' + escapeHtml(item.handle) + '" data-qty="' + escapeHtml(item.quantity) + '">' +
          (imgSrc ? '<a href="' + escapeHtml(item.url) + '" class="lp-cart-item__img"><img src="' + imgSrc + '" alt="' + escapeHtml(item.title) + '" loading="lazy" width="100" height="100"></a>' : '') +
          '<div class="lp-cart-item__info">' +
            '<a href="' + escapeHtml(item.url) + '" class="lp-cart-item__title">' + escapeHtml(displayProductTitle(item.handle, item.product_title)) + '</a>' +
            (variantTitle ? '<span class="lp-cart-item__variant">' + escapeHtml(variantTitle) + '</span>' : '') +
            (sellingPlanName ? '<span class="lp-cart-item__selling-plan">' + escapeHtml(sellingPlanName) + '</span>' : '') +
            '<div class="lp-cart-item__price-row">' +
              (compareLine ? '<s class="lp-cart-item__compare">' + formatMoney(compareLine) + '</s>' : '') +
              (isFree
                ? '<span class="lp-cart-item__free">' + freeLabelText + '</span>'
                : '<span class="lp-cart-item__price">' + formatMoney(item.final_line_price) + '</span>'
              ) +
            '</div>' +
            (isLegacyGift
              ? '<div class="lp-cart-item__qty lp-cart-item__qty--locked"><span class="lp-cart-item__qty-locked">' + lockedGiftText + '</span></div>'
              : '<div class="lp-cart-item__qty">' +
                  '<button type="button" class="lp-cart-item__qty-btn" data-qty-minus aria-label="' + decreaseLabel + '">−</button>' +
                  '<span class="lp-cart-item__qty-num">' + item.quantity + '</span>' +
                  '<button type="button" class="lp-cart-item__qty-btn" data-qty-plus aria-label="' + increaseLabel + '">+</button>' +
                '</div>'
            ) +
          '</div>' +
          '<button type="button" class="lp-cart-item__remove" data-remove aria-label="' + removeLabel + '">' +
            '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
          '</button>' +
        '</div>';
      }).join('');

      Array.from($$('.lp-cart-item[data-key]', itemsWrap)).forEach(function(itemElement) {
        if (pendingItemKeys.has(itemElement.dataset.key)) {
          setItemPending(itemElement, true);
        }
      });

      /* Enrich: fetch compare_at_price from product API (cart.json doesn't include it) */
      enrichCompareAtPrices(itemsWrap);
    }

    /* Progress bar */
    updateProgressBar(cart, drawer);
    updateGiftAssist(cart, drawer);
    if (isOpen) {
      window.requestAnimationFrame(function() {
        refreshDrawerFocusTrap(drawer);
      });
    }
  }

  /* ── Progress bar ── */
  function updateProgressBar(cart, drawer) {
    var bar = $(SELECTORS.progressBar, drawer);
    if (!bar) return;
    var threshold = parseInt(bar.dataset.threshold || '0', 10) * 100; // USD to cents
    if (threshold <= 0) return;

    var fill = $(SELECTORS.progressFill, drawer);
    var msg = $(SELECTORS.progressMsg, drawer);
    var pct = Math.min(100, (cart.total_price / threshold) * 100);
    if (fill) fill.style.width = pct + '%';

    if (msg) {
      var reached = cart.total_price >= threshold;
      var drawer = $(SELECTORS.drawer);
      var awayText = bar.dataset.awayText ||
        (drawer ? drawer.dataset.progressAwayText : '') ||
        'Add [amount] for free shipping';
      var earnedText = bar.dataset.earnedText ||
        (drawer ? drawer.dataset.progressEarnedText : '') ||
        'Free shipping unlocked';
      if (reached) {
        msg.textContent = earnedText;
        bar.classList.add('lp-progress--reached');
      } else {
        var remaining = formatMoney(threshold - cart.total_price);
        msg.textContent = awayText.replace('[amount]', remaining);
        bar.classList.remove('lp-progress--reached');
      }
    }
  }

  function getQtyByHandle(cart, handle) {
    if (!handle || !cart || !cart.items) return 0;
    return cart.items.reduce(function(total, item) {
      return item.handle === handle ? total + item.quantity : total;
    }, 0);
  }

  function updateGiftAssist(cart, drawer) {
    var assist = $('[data-gift-assist]', drawer);
    if (!assist) return;

    var qualifierHandle = assist.dataset.qualifierHandle || '';
    var giftHandle = assist.dataset.giftHandle || '';
    var qualifyingQty = getQtyByHandle(cart, qualifierHandle);
    var giftQty = getQtyByHandle(cart, giftHandle);
    var missingQty = Math.max(0, qualifyingQty - giftQty);
    var countEl = $('[data-gift-count]', assist);
    var helperEl = $('.lp-cart-gift-assist__helper', assist);
    var bodyEl = $('.lp-cart-gift-assist__body', assist);

    if (!qualifyingQty || !missingQty) {
      assist.hidden = true;
      return;
    }

    assist.hidden = false;
    if (countEl) countEl.textContent = String(missingQty);
    if (bodyEl) {
      bodyEl.textContent = missingQty > 1
        ? (assist.dataset.multipleBodyTemplate || '[count] included items are ready to choose.')
            .replace('[count]', String(missingQty))
        : assist.dataset.bodyTemplate || bodyEl.textContent;
    }
    if (helperEl) {
      helperEl.textContent = missingQty > 1
        ? (assist.dataset.multipleHelperTemplate || 'Included quantities match the qualifying product count.')
            .replace('[count]', String(missingQty))
        : assist.dataset.helperTemplate || helperEl.textContent;
    }
  }

  /* ── Enrich cart items with compare_at_price from product API ──
     cart.json doesn't include variant.compare_at_price, so we fetch
     each product once and inject strikethrough prices into the DOM. */
  var _productCache = {};
  function enrichCompareAtPrices(itemsWrap) {
    var items = itemsWrap.querySelectorAll('.lp-cart-item[data-handle]');
    /* Collect unique handles */
    var handles = {};
    items.forEach(function(el) { handles[el.dataset.handle] = true; });

    var fetches = Object.keys(handles).map(function(handle) {
      if (_productCache[handle]) return Promise.resolve(_productCache[handle]);
      return fetch('/products/' + handle + '.json')
        .then(function(r) { return r.json(); })
        .then(function(data) { _productCache[handle] = data; return data; })
        .catch(function() { return null; });
    });

    Promise.all(fetches).then(function() {
      items.forEach(function(el) {
        var handle = el.dataset.handle;
        var variantId = el.dataset.variantId;
        var qty = parseInt(el.dataset.qty || '1', 10);
        var data = _productCache[handle];
        if (!data || !data.product) return;

        var variant = data.product.variants.find(function(v) { return String(v.id) === String(variantId); });
        if (!variant) return;

        var compareAt = parseFloat(variant.compare_at_price);
        var price = parseFloat(variant.price);
        if (!compareAt || compareAt <= price) return;

        /* Already has a compare line? Skip */
        if (el.querySelector('.lp-cart-item__compare')) return;

        /* Insert strikethrough before the price/free label */
        var priceRow = el.querySelector('.lp-cart-item__price-row');
        if (!priceRow) return;
        var compareEl = document.createElement('s');
        compareEl.className = 'lp-cart-item__compare';
        compareEl.textContent = formatMoney(compareAt * 100 * qty);
        priceRow.insertBefore(compareEl, priceRow.firstChild);
      });
    });
  }

  /* ── Event delegation for qty/remove buttons ── */
  function setItemPending(item, isPending) {
    if (!item) return;
    item.dataset.cartPending = isPending ? 'true' : 'false';
    item.setAttribute('aria-busy', isPending ? 'true' : 'false');
    item.querySelectorAll('button').forEach(function(button) {
      button.disabled = isPending;
    });
  }

  function queueItemChange(item, key, quantity, focusSelector) {
    if (!item || pendingItemKeys.has(key)) return;
    pendingItemKeys.add(key);
    setItemPending(item, true);
    cartMutationQueue = cartMutationQueue
      .catch(function() {})
      .then(function() {
        return changeItem(key, quantity, focusSelector);
      })
      .catch(function(err) {
        console.error('Cart change error:', err);
        var drawer = $(SELECTORS.drawer);
        announceCartStatus(drawer ? drawer.dataset.cartUpdateFailedText || 'Could not update cart' : 'Could not update cart');
      })
      .finally(function() {
        pendingItemKeys.delete(key);
        var drawer = $(SELECTORS.drawer);
        var liveItem = drawer ? findCartItemByKey(drawer, key) : null;
        if (liveItem) setItemPending(liveItem, false);
      });
  }

  document.addEventListener('click', function(e) {
    var minusBtn = e.target.closest('[data-qty-minus]');
    var plusBtn = e.target.closest('[data-qty-plus]');
    var removeBtn = e.target.closest('[data-remove]');
    var closeBtn = e.target.closest(SELECTORS.close);
    var backdrop = e.target.closest(SELECTORS.backdrop);

    if (closeBtn || backdrop) { close(); return; }

    var item = (minusBtn || plusBtn || removeBtn) ? (minusBtn || plusBtn || removeBtn).closest('.lp-cart-item') : null;
    if (!item) return;
    var key = item.dataset.key;
    if (!key) return;

    var qtyEl = item.querySelector('.lp-cart-item__qty-num');
    var currentQty = qtyEl ? parseInt(qtyEl.textContent, 10) : 1;

    if (removeBtn) { queueItemChange(item, key, 0, '[data-remove]'); }
    else if (minusBtn) { queueItemChange(item, key, Math.max(0, currentQty - 1), '[data-qty-minus]'); }
    else if (plusBtn) { queueItemChange(item, key, currentQty + 1, '[data-qty-plus]'); }
  });

  /* ── Upsell add in drawer ── */
  document.addEventListener('click', function(e) {
    var upsellBtn = e.target.closest('[data-drawer-upsell-add]');
    if (!upsellBtn) return;
    var vid = upsellBtn.dataset.drawerUpsellAdd;
    if (vid) {
      var drawer = $(SELECTORS.drawer);
      upsellBtn.disabled = true;
      upsellBtn.textContent = drawer ? drawer.dataset.addingLabel || 'Adding...' : 'Adding...';
      addItem(vid).then(function() {
        upsellBtn.textContent = drawer ? drawer.dataset.addedLabel || 'Added' : 'Added';
        setTimeout(function() {
          upsellBtn.textContent = upsellBtn.dataset.originalText || 'Add';
          upsellBtn.disabled = false;
        }, 1500);
      }).catch(function() {
        upsellBtn.textContent = drawer ? drawer.dataset.addFailedLabel || 'Could not add' : 'Could not add';
        setTimeout(function() {
          upsellBtn.textContent = upsellBtn.dataset.originalText || 'Add';
          upsellBtn.disabled = false;
        }, 1500);
      });
    }
  });

  document.addEventListener('click', function(e) {
    var giftBtn = e.target.closest('[data-gift-add]');
    if (!giftBtn) return;
    var variantId = giftBtn.dataset.giftAdd;
    if (!variantId) return;
    giftBtn.disabled = true;
    addItem(variantId, 1).then(function() {
      giftBtn.disabled = false;
    }).catch(function() {
      var drawer = $(SELECTORS.drawer);
      announceCartStatus(drawer ? drawer.dataset.addFailedLabel || 'Could not add' : 'Could not add');
      giftBtn.disabled = false;
    });
  });

  /* ── Global event listeners ── */
  document.addEventListener('cart:open', open);
  document.addEventListener('cart:item-added', function() {
    markCartStateChanged();
    var wasOpen = isOpen;
    open();
    if (wasOpen) fetchAndRender();
  });
  document.addEventListener('cart:updated', function(e) {
    if (!e.detail || typeof e.detail.item_count !== 'number') return;
    markCartStateChanged();
    cartData = e.detail;
    updateHeaderCartState(cartData.item_count);
    render(cartData);
  });
  document.addEventListener('keydown', function(e) { if (e.key === 'Escape' && isOpen) close(); });

  /* ── Auto-update header count + progress bar on page load ── */
  document.addEventListener('DOMContentLoaded', function() {
    fetchAndRender();

    /* ── Shopify theme editor integration ──
       When user selects the cart-drawer section or one of its blocks
       in the theme editor, auto-open the drawer so they can see live
       changes. Also respect the "keep_open" setting. */
    var drawer = $(SELECTORS.drawer);
    if (!drawer) return;

    var keepOpen = drawer.dataset.customizerKeepOpen === 'true';
    if (window.Shopify && Shopify.designMode) {
      if (keepOpen) {
        /* Force open immediately in design mode if toggle is on */
        open();
      }

      /* Shopify editor events */
      document.addEventListener('shopify:section:select', function(e) {
        if (e.target && e.target.querySelector && e.target.querySelector('.lp-cart-drawer')) {
          open();
        }
      });
      document.addEventListener('shopify:section:deselect', function(e) {
        if (!keepOpen && e.target && e.target.querySelector && e.target.querySelector('.lp-cart-drawer')) {
          close();
        }
      });
      document.addEventListener('shopify:block:select', function(e) {
        /* When user selects any block INSIDE the cart drawer */
        if (e.target && e.target.closest && e.target.closest('.lp-cart-drawer')) {
          open();
        }
      });
      document.addEventListener('shopify:block:deselect', function(e) {
        if (!keepOpen && e.target && e.target.closest && e.target.closest('.lp-cart-drawer')) {
          /* Give a brief delay so rapid navigation doesn't flicker */
          setTimeout(function() {
            var anySelected = document.querySelector('.lp-cart-drawer .shopify-block-editor-selected');
            if (!anySelected) close();
          }, 150);
        }
      });
    }
  });
})();
